import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  Info,
  Loader2,
  RefreshCw,
  Shield,
  Users,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { patchAppUser, selectAppUser } from "../../redux/appAuthSlice";
import {
  depositHouseBalance,
  fetchHouseLoanState,
  fetchPartnerBalances,
  withdrawHouseBalance,
} from "../../redux/operatorConsoleSlice";
import { formatCash } from "../../utils/operatorConsoleHelpers";
import {
  canUserRunConsoleTreasuryMove,
  getAcceptedCapitalPartners,
  isBetHousePartnershipReadyForConsole,
  isPartneredHouse,
  maxEqualShareCash,
} from "../../utils/betHousePartners";

const partnerInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name).slice(0, 2).toUpperCase() || "?";
};

const sanitizeAmountInput = (text) => {
  const sanitized = text.replace(/[^0-9.]/g, "");
  const parts = sanitized.split(".");
  if (parts.length > 2) return `${parts[0]}.${parts.slice(1).join("")}`;
  return sanitized;
};

const TreasuryMoveModal = ({ mode, open, onClose, bet, onSuccess }) => {
  const dispatch = useDispatch();
  const operatorUserId = useSelector(selectAppUser)?._id;
  const userCash = Number(useSelector(selectAppUser)?.CASH ?? 0) || 0;

  const isDeposit = mode === "deposit";
  const betId = bet?._id;

  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [partnerBalances, setPartnerBalances] = useState([]);
  const [partnersSyncing, setPartnersSyncing] = useState(false);
  const [loanState, setLoanState] = useState(null);

  const capitalPartners = useMemo(() => getAcceptedCapitalPartners(bet || {}), [bet]);
  const numPartners = capitalPartners.length;
  const hasMultiplePartners = isPartneredHouse(bet || {});
  const canOperateTreasury = canUserRunConsoleTreasuryMove(bet, operatorUserId);
  const partnershipReady = isBetHousePartnershipReadyForConsole(bet);

  const houseAvailable = Number(bet?.amountAvailable || 0);
  const numericAmount = Number(amount);
  const amountValid = amount !== "" && !Number.isNaN(numericAmount) && numericAmount > 0;
  const perPartnerShare =
    amountValid && numPartners > 0 ? maxEqualShareCash(numericAmount, numPartners) : 0;

  const activeLoan = loanState?.activeLoan || null;
  const hasActiveLoan = !isDeposit && !!activeLoan;

  const fetchPartners = useCallback(async () => {
    if (!hasMultiplePartners || !operatorUserId || !betId) return;
    setPartnersSyncing(true);
    try {
      const partners = await dispatch(
        fetchPartnerBalances({ betId, operatorUserId })
      ).unwrap();
      setPartnerBalances(Array.isArray(partners) ? partners : []);
    } catch {
      setPartnerBalances([]);
    } finally {
      setPartnersSyncing(false);
    }
  }, [betId, dispatch, hasMultiplePartners, operatorUserId]);

  const fetchLoan = useCallback(async () => {
    if (isDeposit || !betId) return;
    try {
      const result = await dispatch(fetchHouseLoanState({ betId })).unwrap();
      setLoanState(result?.data || null);
    } catch {
      setLoanState(null);
    }
  }, [betId, dispatch, isDeposit]);

  useEffect(() => {
    if (open) return;
    setAmount("");
    setError("");
    setPartnerBalances([]);
    setLoanState(null);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    fetchPartners();
    fetchLoan();

    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, dispatch, fetchPartners, fetchLoan, onClose, submitting]);

  const handleAmountChange = (text) => {
    const normalized = sanitizeAmountInput(text);
    if (normalized === "" || normalized === ".") {
      setAmount(normalized);
      setError("");
      return;
    }
    const parsed = Number(normalized);
    if (Number.isNaN(parsed)) {
      setAmount(normalized);
      setError("");
      return;
    }
    if (!isDeposit && parsed > houseAvailable) {
      setAmount(String(houseAvailable));
      setError("");
      return;
    }
    setAmount(normalized);
    setError("");
  };

  const validateAmount = () => {
    if (!amount.trim()) {
      setError("Amount is required");
      return false;
    }
    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      setError("Enter a positive amount");
      return false;
    }
    if (isDeposit) {
      if (hasMultiplePartners) {
        const myShare = maxEqualShareCash(numericAmount, numPartners);
        if (userCash + 1e-9 < myShare) {
          setError(
            `Your share is ${formatCash(myShare, { compact: true })} but your wallet has ${formatCash(userCash, { compact: true })}.`
          );
          return false;
        }
      } else if (numericAmount > userCash) {
        setError(
          `Insufficient funds — you have ${formatCash(userCash, { compact: true })} Cash`
        );
        return false;
      }
    } else if (numericAmount > houseAvailable) {
      setError(
        `House only has ${formatCash(houseAvailable, { compact: true })} Cash available`
      );
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAmount() || !betId || !operatorUserId) return;

    setSubmitting(true);
    try {
      const thunk = isDeposit ? depositHouseBalance : withdrawHouseBalance;
      const result = await dispatch(
        thunk({ betId, operatorUserId, amount: numericAmount })
      ).unwrap();

      if (result.userCash != null) {
        dispatch(patchAppUser({ CASH: result.userCash }));
      }

      onSuccess?.({ message: result.message, mode });
      onClose();
    } catch (err) {
      setError(String(err || (isDeposit ? "Deposit failed" : "Withdrawal failed")));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMax = () => {
    if (isDeposit) {
      const maxDeposit = hasMultiplePartners
        ? userCash * numPartners
        : userCash;
      if (maxDeposit > 0) {
        setAmount(String(maxDeposit));
        setError("");
      }
      return;
    }
    if (houseAvailable > 0) {
      setAmount(String(houseAvailable));
      setError("");
    }
  };

  if (!open) return null;

  if (!bet) {
    return createPortal(
      <div className="oc-treasury-modal-root" role="dialog" aria-modal="true">
        <button
          type="button"
          className="oc-treasury-modal-backdrop"
          aria-label="Close"
          onClick={onClose}
        />
        <div className="oc-treasury-modal-sheet">
          <div className="oc-treasury-modal-body">
            <p>House data is still loading. Close and try again.</p>
            <button type="button" className="oc-treasury-cancel" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const remainingAvailable = Math.max(
    0,
    houseAvailable - (!isDeposit && amountValid ? numericAmount : 0)
  );
  const isWithdrawingPreview = !isDeposit && amountValid && numericAmount > 0;
  const submitLocked = submitting;
  const submitDisabled =
    submitLocked ||
    !canOperateTreasury ||
    !partnershipReady ||
    hasActiveLoan ||
    !amountValid ||
    (!isDeposit && numericAmount > houseAvailable);

  const title = isDeposit ? "Deposit funds" : "Withdraw funds";
  const Icon = isDeposit ? ArrowUpCircle : ArrowDownCircle;

  return createPortal(
    <div
      className="oc-treasury-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="oc-treasury-modal-title"
    >
      <button
        type="button"
        className="oc-treasury-modal-backdrop"
        aria-label="Close"
        onClick={() => !submitting && onClose()}
        disabled={submitting}
      />
      <div
        className={`oc-treasury-modal-sheet ${submitting ? "oc-treasury-modal-sheet-busy" : ""}`}
        aria-busy={submitting}
      >
        {submitting ? (
          <div className="oc-treasury-modal-busy" aria-live="polite">
            <Loader2 size={28} className="oc-treasury-modal-spin" aria-hidden />
            <strong>{isDeposit ? "Depositing…" : "Withdrawing…"}</strong>
            <span>Waiting for server confirmation</span>
          </div>
        ) : null}

        <header className="oc-treasury-modal-head">
          <div className="oc-treasury-modal-head-main">
            <span className="oc-treasury-modal-icon">
              <Icon size={18} />
            </span>
            <div>
              <p className="oc-treasury-modal-eyebrow">Treasury</p>
              <h2 id="oc-treasury-modal-title">{title}</h2>
              <p className="oc-treasury-modal-sub">{bet.name || "Bet house"}</p>
            </div>
          </div>
          <button
            type="button"
            className="oc-treasury-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="oc-treasury-modal-body">
          {!canOperateTreasury ? (
            <div className="oc-treasury-banner oc-treasury-banner-info">
              <Info size={14} />
              <p>Only the house owner can deposit or withdraw from the treasury.</p>
            </div>
          ) : null}

          {!partnershipReady ? (
            <div className="oc-treasury-banner oc-treasury-banner-info">
              <Info size={14} />
              <p>
                All partners must accept their invite before you can move treasury funds.
              </p>
            </div>
          ) : null}

          {hasActiveLoan ? (
            <div className="oc-treasury-banner oc-treasury-banner-warn">
              <AlertCircle size={14} />
              <p>
                Withdrawals are paused while a house loan is active (
                {formatCash(activeLoan?.remainingTotal || 0, { compact: true })} remaining).
                Pay it off in the Loan section first.
              </p>
            </div>
          ) : null}

          <div className="oc-treasury-stats">
            <div
              className={`oc-treasury-stat ${isWithdrawingPreview ? "oc-treasury-stat-live" : "oc-treasury-stat-accent"}`}
            >
              <em>{isWithdrawingPreview ? "Remaining" : "House available"}</em>
              <strong className="tabular-nums">
                {formatCash(isWithdrawingPreview ? remainingAvailable : houseAvailable, {
                  compact: true,
                })}
              </strong>
              {isWithdrawingPreview ? (
                <span className="oc-treasury-stat-was tabular-nums">
                  was {formatCash(houseAvailable, { compact: true })}
                </span>
              ) : (
                <span className="oc-treasury-stat-unit">Cash</span>
              )}
            </div>
            <div className="oc-treasury-stat">
              <em>Your wallet</em>
              <strong className="tabular-nums">{formatCash(userCash, { compact: true })}</strong>
              <span className="oc-treasury-stat-unit">Cash</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="oc-treasury-field">
              <div className="oc-treasury-field-head">
                <label htmlFor="oc-treasury-amount">
                  Amount to {isDeposit ? "deposit" : "withdraw"}
                </label>
                {canOperateTreasury && partnershipReady && !hasActiveLoan ? (
                  <button type="button" className="oc-treasury-max" onClick={handleMax}>
                    Max
                  </button>
                ) : null}
              </div>
              <div className={`oc-treasury-input ${error ? "oc-treasury-input-error" : ""}`}>
                <input
                  id="oc-treasury-amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  disabled={submitLocked}
                />
                <span>CASH</span>
              </div>
              {error ? (
                <p className="oc-treasury-error">
                  <AlertCircle size={12} />
                  {error}
                </p>
              ) : (
                <p className="oc-treasury-hint">
                  {hasMultiplePartners
                    ? perPartnerShare > 0
                      ? isDeposit
                        ? `Each partner pays ${formatCash(perPartnerShare, { compact: true })} Cash`
                        : `Each partner receives ${formatCash(perPartnerShare, { compact: true })} Cash`
                      : `Split equally among ${numPartners} partners`
                    : isDeposit
                    ? `From your wallet · max ${formatCash(userCash, { compact: true })}`
                    : `Max: ${formatCash(houseAvailable, { compact: true })} Cash`}
                </p>
              )}
            </div>

            {hasMultiplePartners ? (
              <section className="oc-treasury-partners">
                <header>
                  <Users size={14} />
                  <div>
                    <em>Capital</em>
                    <strong>Partner split</strong>
                  </div>
                  <span className="oc-treasury-partners-count">{numPartners}</span>
                  <button
                    type="button"
                    className="oc-treasury-refresh"
                    onClick={fetchPartners}
                    disabled={partnersSyncing}
                    aria-label="Refresh partner balances"
                  >
                    <RefreshCw size={14} className={partnersSyncing ? "oc-treasury-modal-spin" : ""} />
                  </button>
                </header>

                {partnersSyncing && partnerBalances.length === 0 ? (
                  <div className="oc-treasury-partners-loading">
                    <Loader2 size={16} className="oc-treasury-modal-spin" />
                    <span>Syncing balances…</span>
                  </div>
                ) : partnerBalances.length > 0 ? (
                  <ul className="oc-treasury-partners-list">
                    {partnerBalances.map((partner, idx) => {
                      const hasEnough =
                        isDeposit && perPartnerShare > 0
                          ? Number(partner.cash) + 0.005 >= perPartnerShare
                          : null;
                      return (
                        <li key={String(partner.userId || idx)}>
                          <span className="oc-treasury-partner-avatar">
                            {partnerInitials(partner.name)}
                          </span>
                          <div className="oc-treasury-partner-copy">
                            <strong>{partner.name || "Partner"}</strong>
                            <span>
                              Wallet · {formatCash(partner.cash, { compact: true })}
                              {isDeposit && hasEnough === false ? (
                                <em className="oc-treasury-partner-short"> Short</em>
                              ) : null}
                            </span>
                          </div>
                          <div className="oc-treasury-partner-share">
                            {perPartnerShare > 0 ? (
                              <strong className={isDeposit ? "oc-treasury-share-out" : "oc-treasury-share-in"}>
                                {isDeposit ? "−" : "+"}
                                {formatCash(perPartnerShare, { compact: true })}
                              </strong>
                            ) : (
                              <span>—</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <button type="button" className="oc-treasury-partners-empty" onClick={fetchPartners}>
                    Tap to load partner balances
                  </button>
                )}
              </section>
            ) : null}

            <div className="oc-treasury-caution">
              <Shield size={14} />
              <p>
                {hasMultiplePartners
                  ? `${isDeposit ? "Deposit" : "Withdrawal"} is split equally among ${numPartners} partners. ${
                      isDeposit
                        ? "Every partner must have enough Cash in their wallet."
                        : "Ensure the house can still cover active bets."
                    }`
                  : isDeposit
                  ? "Cash moves from your wallet into the house treasury."
                  : "Make sure the house can still cover active bet liabilities before withdrawing."}
              </p>
            </div>

            {amountValid && !error ? (
              <div className="oc-treasury-preview">
                <em>Preview</em>
                <strong>After {isDeposit ? "deposit" : "withdrawal"}</strong>
                <div className="oc-treasury-preview-row">
                  <span>{hasMultiplePartners ? "Each partner" : "You"}</span>
                  <strong className={isDeposit ? "oc-treasury-share-out" : "oc-treasury-share-in"}>
                    {isDeposit ? "−" : "+"}
                    {formatCash(hasMultiplePartners ? perPartnerShare : numericAmount, {
                      compact: true,
                    })}{" "}
                    Cash
                  </strong>
                </div>
                <div className="oc-treasury-preview-row">
                  <span>House balance after</span>
                  <strong className="tabular-nums">
                    {formatCash(
                      isDeposit
                        ? houseAvailable + numericAmount
                        : remainingAvailable,
                      { compact: true }
                    )}{" "}
                    Cash
                  </strong>
                </div>
              </div>
            ) : null}

            <div className="oc-treasury-modal-foot">
              <button type="button" className="oc-treasury-cancel" onClick={onClose} disabled={submitLocked}>
                Cancel
              </button>
              <button
                type="submit"
                className="oc-treasury-submit"
                disabled={submitDisabled}
              >
                {isDeposit ? "Deposit" : "Withdraw"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TreasuryMoveModal;
